import type { AuthTokens } from "@orbit/api-zod";

const STORAGE_KEY = "orbit.auth.v1";
const EXPIRED_FLAG_KEY = "orbit.auth.expired";

interface Stored {
  customerId: string;
  tokens: AuthTokens;
}

export function readStored(): Stored | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    if (!parsed.customerId || !parsed.tokens?.accessToken) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeStored(s: Stored): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  window.dispatchEvent(new Event("orbit:auth-changed"));
}

/**
 * Update only the tokens portion of the stored auth, preserving customerId.
 * Used by the silent-refresh interceptor so we don't lose track of who's
 * signed in across a token rotation.
 */
export function updateStoredTokens(tokens: AuthTokens): void {
  const existing = readStored();
  if (!existing) return;
  writeStored({ customerId: existing.customerId, tokens });
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event("orbit:auth-changed"));
}

export function getAccessToken(): string | null {
  return readStored()?.tokens.accessToken ?? null;
}

export function getRefreshToken(): string | null {
  return readStored()?.tokens.refreshToken ?? null;
}

/**
 * Marks the session as having been forcibly ended (refresh failed). The
 * Login page reads + clears this flag so it can show a friendly banner
 * instead of a blank welcome.
 */
export function setSessionExpiredFlag(): void {
  try {
    sessionStorage.setItem(EXPIRED_FLAG_KEY, "1");
  } catch {
    // ignore quota / privacy mode
  }
}

export function consumeSessionExpiredFlag(): boolean {
  try {
    const v = sessionStorage.getItem(EXPIRED_FLAG_KEY);
    if (v) sessionStorage.removeItem(EXPIRED_FLAG_KEY);
    return v === "1";
  } catch {
    return false;
  }
}
